import React, { SyntheticEvent, useContext, useRef } from 'react';
import Modal from '@xo-union/tk-component-modals';
import { compose } from '@xo-union/react-css-modules';
import { FlexImageCropper } from '../../../libs/gs-component-image-upload';
import { UploadInfo } from '../../../libs/gs-component-image-upload';
import Spinner from '@xo-union/tk-component-spinner';

import styles from './index.scss';
import CropperPhotoRotation from './CropperPhotoRotation';
import PhotoCropRatios from './PhotoCropRatios';
import CropperPhotoActions from './CropperPhotoActions';
import { SafeCropContext } from '../../SafeCrop';
import Alert from '@xo-union/tk-component-alerts';
import LayoutToggle, { HORIZONTAL_ICON, HORIZONTAL_RATIO } from './LayoutToggle';
import { RotationControl } from './RotationControl';
import { ReplaceControl } from './ReplaceControl';
import { DeleteControl } from './DeleteControl';

interface CropperInfo {
  file: HTMLImageElement;
  onSave: (info: any) => Promise<void>;
}

interface Props {
  cropperInfo: CropperInfo;
  onCancel: () => void;
  pageId: number;
  itemType: string;
  isCoverPhoto?: boolean;
  currentAspectRatio?: { name: string; value: number } | null;
  primaryActionText?: string;
  secondaryActionText?: string;
  loading?: boolean;
  children?: React.ReactNode | React.ReactNode[] | null;
  ref?: any;
  defaultLayoutRatioValue?: number;
  defaultLayoutRatioName?: string;
  onDeletePhotoHandler?: (e: SyntheticEvent<Element, Event>) => void;
  onCloseCropper?: () => void;
  onAddPhoto?: (uploadInfo: UploadInfo) => void;
  trackCoverPhotoAction?: (action: string, ratioName: string) => void;
}

const PhotoCropper: React.FC<Props> = React.forwardRef(
  (
    {
      cropperInfo,
      onCancel,
      pageId,
      itemType,
      isCoverPhoto = false,
      currentAspectRatio,
      primaryActionText,
      secondaryActionText,
      loading,
      children,
      defaultLayoutRatioName = '1:1',
      defaultLayoutRatioValue = 1,
      onDeletePhotoHandler,
      onCloseCropper,
      onAddPhoto,
      trackCoverPhotoAction,
    },
    ref
  ) => {
    const localRef = useRef<any>(null);
    const alertRef = ref || localRef;
    const { getSafeCrop } = useContext(SafeCropContext);
    const safeCrop = getSafeCrop(pageId, itemType);
    let lookup = safeCrop?.aspectRatios?.reduce<{ [index: string]: number }>((acc, a) => {
      acc[a.name] = a.value;
      return acc;
    }, {});

    let defaultRatioName = currentAspectRatio?.name || safeCrop?.defaultAspectRatio?.name;
    let defaultRatioValue = currentAspectRatio?.value || safeCrop?.defaultAspectRatio?.value;

    defaultRatioName = defaultLayoutRatioName || HORIZONTAL_ICON;
    defaultRatioValue = defaultLayoutRatioValue || HORIZONTAL_RATIO;

    const classes = compose({
      'close-button': styles.closeButton,
      medium: styles.modal,
    });

    const trackCoverPhotoRotation = () => {
      trackCoverPhotoAction && trackCoverPhotoAction('rotate_photo', defaultRatioName || HORIZONTAL_ICON);
    };

    const modalTitle = isCoverPhoto ? 'Adjust cover photo' : 'Adjust Photo';

    return (
      <Modal classes={classes} className={styles.modal} size="lg">
        <FlexImageCropper {...cropperInfo} className={styles.cropper} aspectRatio={defaultRatioValue}>
          {(editor): React.ReactElement => (
            <>
              <div data-testid="photo-cropper" className={styles.header}>
                <h1 className={styles.title}>{modalTitle}</h1>
                <div className={styles.options}>
                  {isCoverPhoto ? (
                    <LayoutToggle
                      defaultRatioName={defaultRatioName}
                      defaultRatio={defaultRatioValue}
                      trackCoverPhotoAction={trackCoverPhotoAction}
                    />
                  ) : (
                    <>
                      <CropperPhotoRotation trackCoverPhotoRotation={trackCoverPhotoRotation} />
                      <PhotoCropRatios ratioLookup={lookup} defaultRatio={defaultRatioName} />
                    </>
                  )}
                </div>
              </div>
              <div className={styles.main}>{editor}</div>
              <div className={styles.footer}>
                {loading ? (
                  <div className={styles.loading}>
                    <Spinner data-testid="loading-spinner" />
                  </div>
                ) : (
                  <>
                    <div className={styles.modalFooterContainer}>
                      {children}
                      <div className={styles.photoActionButtonWrapper}>
                        {isCoverPhoto && (
                          <>
                            <RotationControl trackCoverPhotoRotation={trackCoverPhotoRotation} />
                            <ReplaceControl onAddPhoto={onAddPhoto} />
                          </>
                        )}
                        {onDeletePhotoHandler && (
                          <DeleteControl onPhotoDelete={onDeletePhotoHandler} onCloseCropper={onCloseCropper} />
                        )}
                      </div>
                      <CropperPhotoActions
                        primaryActionText={primaryActionText}
                        secondaryActionText={secondaryActionText}
                        onCancel={onCancel}
                      />
                    </div>
                    <Alert ref={alertRef} position="bottom" />
                  </>
                )}
              </div>
            </>
          )}
        </FlexImageCropper>
      </Modal>
    );
  }
);

export default PhotoCropper;
